import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemDecorator,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/joy';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Plus as AddIcon,
  Sparkle as AIIcon,
  Lightbulb as TipIcon,
} from '@phosphor-icons/react';

interface Suggestion {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  type: 'missing' | 'improvement' | 'seo' | 'performance';
  action?: () => void;
  completed?: boolean;
}

interface ContentSuggestionsProps {
  siteId: number;
  pages: any[];
  onAddContent: (suggestion: Suggestion) => void;
}

export const ContentSuggestions: React.FC<ContentSuggestionsProps> = ({
  siteId,
  pages,
  onAddContent,
}) => {
  // Analyze site content and generate suggestions
  const suggestions: Suggestion[] = [
    // Check for missing essential pages
    ...(pages.find(p => p.slug === 'index') ? [] : [{
      id: 'homepage',
      title: 'Create Homepage',
      description: 'Every website needs a homepage to welcome visitors',
      priority: 'high' as const,
      type: 'missing' as const,
    }]),
    ...(pages.find(p => p.slug === 'contact') ? [] : [{
      id: 'contact',
      title: 'Add Contact Page',
      description: 'Make it easy for customers to reach you',
      priority: 'high' as const,
      type: 'missing' as const,
    }]),
    ...(pages.find(p => p.slug === 'services') ? [] : [{
      id: 'services',
      title: 'Create Services Page',
      description: 'Showcase all your roofing services in one place',
      priority: 'high' as const,
      type: 'missing' as const,
    }]),
    
    // Check for content improvements
    ...(pages.length < 5 ? [{
      id: 'more-pages',
      title: 'Add More Pages',
      description: 'Google favors sites with 5+ pages. Consider adding About Us, Gallery, or FAQ',
      priority: 'medium' as const,
      type: 'seo' as const,
    }] : []),
    
    // Check for missing testimonials
    {
      id: 'testimonials',
      title: 'Add Customer Reviews',
      description: 'Social proof increases conversions by up to 270%',
      priority: 'high' as const,
      type: 'improvement' as const,
      completed: pages.some(p => p.sections_draft?.some((s: any) => s.type === 'testimonials')),
    },
    
    // Check for missing photos
    {
      id: 'photos',
      title: 'Add Project Photos',
      description: 'Visual content gets 94% more views than text-only',
      priority: 'medium' as const,
      type: 'improvement' as const,
      completed: pages.some(p => p.sections_draft?.some((s: any) => s.type === 'job_showcase')),
    },
    
    // SEO suggestions
    {
      id: 'local-seo',
      title: 'Create Location Pages',
      description: 'Rank higher in "[City] roofing" searches with dedicated location pages',
      priority: 'medium' as const,
      type: 'seo' as const,
      completed: pages.some(p => p.template_key === 'location'),
    },
    
    // Performance suggestions
    {
      id: 'special-offer',
      title: 'Add Special Offer',
      description: 'Limited-time offers create urgency and boost conversions',
      priority: 'low' as const,
      type: 'improvement' as const,
      completed: pages.some(p => p.slug.includes('offer') || p.slug.includes('special')),
    },
  ].filter(s => !s.completed);

  const completedCount = 8 - suggestions.length;
  const completionPercentage = (completedCount / 8) * 100;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'neutral';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'missing': return <WarningIcon />;
      case 'improvement': return <TipIcon />;
      case 'seo': return <AIIcon />;
      default: return <AddIcon />;
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography level="title-lg">Content Health Check</Typography>
          <Chip color="primary" variant="soft">
            {completedCount}/8 Complete
          </Chip>
        </Box>

        <LinearProgress
          determinate
          value={completionPercentage}
          sx={{ mb: 3 }}
          color={completionPercentage === 100 ? 'success' : 'primary'}
        />

        {suggestions.length === 0 ? (
          <Alert color="success" startDecorator={<CheckIcon />}>
            Excellent! Your website has all recommended content.
          </Alert>
        ) : (
          <>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Here's what will make your website even better:
            </Typography>

            <List size="sm">
              {suggestions.map((suggestion) => (
                <ListItem key={suggestion.id}>
                  <ListItemDecorator>
                    {getTypeIcon(suggestion.type)}
                  </ListItemDecorator>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography level="body-md" fontWeight="medium">
                        {suggestion.title}
                      </Typography>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={getPriorityColor(suggestion.priority) as any}
                      >
                        {suggestion.priority} priority
                      </Chip>
                    </Box>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {suggestion.description}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => onAddContent(suggestion)}
                  >
                    Add
                  </Button>
                </ListItem>
              ))}
            </List>

            <Alert sx={{ mt: 3 }} variant="soft" color="primary" startDecorator={<AIIcon />}>
              <Box>
                <Typography level="body-sm" fontWeight="bold">
                  Pro Tip: Use AI to generate content instantly!
                </Typography>
                <Typography level="body-xs">
                  Click any "Add" button and our AI will create professional content tailored to your business.
                </Typography>
              </Box>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
};
